import axios from '../modules/Shared/auth-header';

/**
 * API Service for Service Monitoring
 * Handles all HTTP requests to monitoring endpoints
 * Uses the application's auth-header axios instance for authentication
 */

/**
 * Get all monitored services
 * @returns {Promise<ServiceConfigDto[]>}
 */
export const getAllServices = async () => {
  const response = await axios.get('/api/monitoring/services');
  return response.data;
};

/**
 * Get specific service by name
 * @param {string} serviceName - Service name
 * @returns {Promise<ServiceConfigDto>}
 */
export const getServiceByName = async (serviceName) => {
  const response = await axios.get(`/api/monitoring/services/${encodeURIComponent(serviceName)}`);
  return response.data;
};

/**
 * Get execution history for a service
 * @param {string} serviceName - Service name
 * @param {number} limit - Maximum number of records (default: 10)
 * @returns {Promise<ExecutionHistoryDto[]>}
 */
export const getExecutionHistory = async (serviceName, limit = 10) => {
  const response = await axios.get(
    `/api/monitoring/services/${encodeURIComponent(serviceName)}/history`,
    { params: { limit } }
  );
  return response.data;
};

/**
 * Get service events (Windows Event Log entries)
 * @param {string} serviceName - Service name
 * @param {number} limit - Maximum number of events (default: 10)
 * @returns {Promise<ServiceEventDto[]>}
 */
export const getServiceEvents = async (serviceName, limit = 10) => {
  const response = await axios.get(
    `/api/monitoring/services/${encodeURIComponent(serviceName)}/events`,
    { params: { limit } }
  );
  return response.data;
};

/**
 * Check monitoring module health status
 * Returns whether monitoring is enabled and configured properly
 * No authentication required - useful for startup checks
 * @returns {Promise<{Status: string, MonitoringEnabled: boolean, Message: string, Recommendation: string}>}
 */
export const checkMonitoringHealth = async () => {
  const response = await axios.get('/api/monitoring/health');
  return response.data;
};

/**
 * Get dashboard summary statistics
 * Used by sidebar indicator and summary cards
 * @returns {Promise<DashboardSummaryDto>}
 */
export const getDashboardSummary = async () => {
  const response = await axios.get('/api/monitoring/dashboard/summary');
  return response.data;
};

/**
 * Get service health status
 * @returns {Promise<ServiceHealthDto[]>}
 */
export const getServiceHealth = async () => {
  const response = await axios.get('/api/monitoring/dashboard/health');
  return response.data;
};

/**
 * Get recent email alerts
 * @param {number} hours - Number of hours to look back (default: 24, max: 168)
 * @returns {Promise<AlertDto[]>}
 */
export const getRecentAlerts = async (hours = 24) => {
  const response = await axios.get('/api/monitoring/alerts/recent', {
    params: { hours }
  });
  return response.data;
};

/**
 * Mark an alert as viewed (user clicked email link and viewed dashboard)
 * @param {number} alertId - Alert ID from ServiceAlerts table
 * @returns {Promise<Object>}
 */
export const markAlertAsViewed = async (alertId) => {
  const response = await axios.post(`/api/monitoring/alerts/${alertId}/mark-viewed`);
  return response.data;
};

/**
 * Get email configuration (enabled/disabled status)
 * @returns {Promise<EmailConfigurationDto>}
 */
export const getEmailConfiguration = async () => {
  const response = await axios.get('/api/monitoring/email/config');
  return response.data;
};

/**
 * Toggle email sending on/off
 * @param {boolean} enabled - true to enable emails, false to disable
 * @param {string} updatedBy - Username of person making the change
 * @returns {Promise<Object>}
 */
export const toggleEmailConfiguration = async (enabled, updatedBy = 'Admin') => {
  const response = await axios.post('/api/monitoring/email/config/toggle', {
    enabled,
    updatedBy
  });
  return response.data;
};

/**
 * Get email logs (both sent and logged-only emails)
 * @param {number} hours - Number of hours to look back (default: 24, max: 168)
 * @returns {Promise<EmailLogDto[]>}
 */
export const getEmailLogs = async (hours = 24) => {
  const response = await axios.get('/api/monitoring/email/logs', {
    params: { hours }
  });
  return response.data;
};

/**
 * Get email logs with pagination
 * @param {number} hours - Number of hours to look back (default: 24, max: 168)
 * @param {number} page - Page number (starting from 1)
 * @param {number} pageSize - Number of records per page
 * @returns {Promise<PaginatedResponse<EmailLogDto>>}
 */
export const getEmailLogsPaginated = async (hours = 24, page = 1, pageSize = 10) => {
  const response = await axios.get('/api/monitoring/email/logs', {
    params: { hours, page, pageSize }
  });
  return response.data;
};

/**
 * Get specific email log by ID
 * @param {number} emailLogId - Email log ID
 * @returns {Promise<EmailLogDto>}
 */
export const getEmailLogById = async (emailLogId) => {
  const response = await axios.get(`/api/monitoring/email/logs/${emailLogId}`);
  return response.data;
};

export default {
  getAllServices,
  getServiceByName,
  getExecutionHistory,
  getServiceEvents,
  checkMonitoringHealth,
  getDashboardSummary,
  getServiceHealth,
  getRecentAlerts,
  markAlertAsViewed,
  getEmailConfiguration,
  toggleEmailConfiguration,
  getEmailLogs,
  getEmailLogsPaginated,
  getEmailLogById,
};
